// admin/src/pages/payments/PaymentsHistory.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  Spinner,
  Alert,
  FormControl,
  Dropdown,
} from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../services/api";
import { formatCurrency } from "../utils/format";
import { Colors } from "../constants/Colors";
import {
  PER_PAGE,
  PaymentsStyles,
  StatsRow,
  PaginationBar,
  PaymentDetailsModal,
  StatusPill,
  MethodPill,
  TypeChip,
  RefundBadge,
  Avatar,
  getPaymentType,
  getRefundState,
  getRefundInfo,
  isRefunded,
  getPaymentId,
  getPaymentDate,
  getPassengerName,
  getOperatorName,
  formatShortDate,
  getDefaultDateRange,
  parseList,
} from "../utils/paymentShared";

const MySwal = withReactContent(Swal);

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "succeeded", label: "Succeeded" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
];

const METHOD_OPTIONS = [
  { value: "all", label: "All Methods" },
  { value: "gcash", label: "GCash" },
  { value: "qrph", label: "QR Ph" },
  { value: "cash", label: "Cash" },
  { value: "manual", label: "Manual" },
];

const TYPE_OPTIONS = [
  { value: "all", label: "All Types" },
  { value: "rental", label: "Rental" },
  { value: "cancellation_fee", label: "Cancellation Fee" },
];

const labelOf = (options, value, fallback) =>
  options.find((o) => o.value === value)?.label || fallback;

/** Fallback when the backend does not send `totals`: numbers for the current page only. */
const pageTotals = (rows, total) => {
  const t = {
    total,
    succeeded: 0,
    pending: 0,
    failed: 0,
    refunded: 0,
    collected_amount: 0,
    refunded_amount: 0,
  };
  rows.forEach((p) => {
    const amt = Number(p.amount) || 0;
    if (isRefunded(p)) {
      t.refunded += 1;
      t.refunded_amount += amt;
    } else if (p.status === "succeeded") {
      t.succeeded += 1;
      t.collected_amount += amt;
    } else if (p.status === "pending") t.pending += 1;
    else if (p.status === "failed") t.failed += 1;
  });
  return t;
};

const PaymentsHistory = () => {
  const defaults = getDefaultDateRange();

  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState(null); // from backend, covers ALL pages
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: PER_PAGE,
    total: 0,
  });

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const requestIdRef = useRef(0);
  const viewLockRef = useRef(false);

  const loadHistory = useCallback(
    async (page = 1, overrides = {}) => {
      const reqId = ++requestIdRef.current;

      const params = {
        page,
        per_page: PER_PAGE,
        date_from: overrides.dateFrom ?? dateFrom,
        date_to: overrides.dateTo ?? dateTo,
        status: overrides.statusFilter ?? statusFilter,
        payment_method: overrides.methodFilter ?? methodFilter,
        payment_type: overrides.typeFilter ?? typeFilter,
      };

      Object.keys(params).forEach((k) => {
        if (
          params[k] === "" ||
          params[k] === "all" ||
          params[k] === undefined ||
          params[k] === null
        ) {
          delete params[k];
        }
      });

      try {
        setLoading(true);
        setError(null);
        const res = await api.get("/payments/history", { params });
        if (reqId !== requestIdRef.current) return;

        const { rows, pagination: pg, error: hasError } = parseList(res.data);

        if (hasError) {
          setError(res.data?.message || "Failed to load payment history");
          setPayments([]);
          setTotals(null);
          return;
        }

        // The backend already applies status / method / type / date filters.
        // Filtering again here (after pagination) only made pages and counts disagree.
        setPayments(rows);
        setTotals(res.data?.totals || null);
        setPagination(
          pg || {
            current_page: page,
            last_page: 1,
            per_page: PER_PAGE,
            total: rows.length,
          },
        );
      } catch (err) {
        if (reqId !== requestIdRef.current) return;
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to load payment history.",
        );
        setPayments([]);
        setTotals(null);
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    },
    [dateFrom, dateTo, statusFilter, methodFilter, typeFilter],
  );

  useEffect(() => {
    loadHistory(1);
  }, [loadHistory]);

  const handleApplyFilters = (e) => {
    if (e) e.preventDefault();
    loadHistory(1);
  };

  const handleResetFilters = () => {
    const d = getDefaultDateRange();
    setDateFrom(d.from);
    setDateTo(d.to);
    setStatusFilter("all");
    setMethodFilter("all");
    setTypeFilter("all");
    loadHistory(1, {
      dateFrom: d.from,
      dateTo: d.to,
      statusFilter: "all",
      methodFilter: "all",
      typeFilter: "all",
    });
  };

  const handlePageChange = (n) => {
    if (n >= 1 && n <= pagination.last_page) loadHistory(n);
  };

  const exportReport = async () => {
    try {
      setExporting(true);
      const res = await api.get("/payments/history/export", {
        params: {
          date_from: dateFrom,
          date_to: dateTo,
          status: statusFilter !== "all" ? statusFilter : undefined,
          payment_method: methodFilter !== "all" ? methodFilter : undefined,
          payment_type: typeFilter !== "all" ? typeFilter : undefined,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "text/csv" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `payment_history_${dateFrom}_to_${dateTo}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      MySwal.fire({
        icon: "success",
        title: "Exported",
        text: "Payment history downloaded.",
        timer: 2500,
        showConfirmButton: false,
      });
    } catch (err) {
      MySwal.fire({
        icon: "error",
        title: "Export Failed",
        text: err.response?.data?.message || "Failed to export.",
        confirmButtonColor: Colors.PRIMARY,
      });
    } finally {
      setExporting(false);
    }
  };

  const viewDetails = (payment) => {
    if (viewLockRef.current) return;
    viewLockRef.current = true;
    setSelectedPayment(payment);
    setDetailOpen(true);
    setTimeout(() => {
      viewLockRef.current = false;
    }, 400);
  };

  const getRowClass = (p) => {
    const state = getRefundState(p);
    if (state === "pending") return "row-refund-pending";
    if (state === "refunded") return "row-refunded";
    if (getPaymentType(p) === "cancellation_fee") return "row-cancellation";
    return "";
  };

  /* ---------------- stats ---------------- */
  const t = totals || pageTotals(payments, pagination.total);
  const scope = totals ? "" : " (page)";

  const statCards = [
    {
      k: "Total Payments",
      v: Number(t.total ?? pagination.total).toLocaleString(),
      i: "bi-receipt",
      c: Colors.PRIMARY,
    },
    {
      k: `Succeeded${scope}`,
      v: Number(t.succeeded).toLocaleString(),
      i: "bi-check-circle",
      c: Colors.SUCCESS,
    },
    {
      k: `Pending${scope}`,
      v: Number(t.pending).toLocaleString(),
      i: "bi-clock-history",
      c: Colors.WARNING,
    },
    {
      k: `Failed${scope}`,
      v: Number(t.failed).toLocaleString(),
      i: "bi-x-circle",
      c: Colors.RED,
    },
    {
      k: `Refunded${scope}`,
      v: Number(t.refunded).toLocaleString(),
      i: "bi-arrow-counterclockwise",
      c: Colors.INFO,
      title: `${formatCurrency(t.refunded_amount)} refunded`,
    },
    {
      k: `Collected${scope}`,
      v: formatCurrency(t.collected_amount),
      i: "bi-cash-stack",
      c: Colors.SECONDARY_BLUE,
      isText: true,
      title: "Succeeded payments, excluding refunds",
    },
  ];

  return (
    <div className="bk-page">
      <PaymentsStyles />
      <Container fluid>
        <div className="bk-header">
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h2>Payment History</h2>
            <p>All payment transactions across the platform</p>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <i className="bi bi-exclamation-triangle me-2" />
                {error}
              </div>
              <Button
                className="bk-btn-ghost"
                size="sm"
                onClick={() => loadHistory(pagination.current_page)}
              >
                <i className="bi bi-arrow-clockwise me-1" />
                Retry
              </Button>
            </div>
          </Alert>
        )}

        <StatsRow cards={statCards} loading={loading} />

        {/* ---------- Filters ---------- */}
        <div className="bk-toolbar">
          <Form onSubmit={handleApplyFilters}>
            <Row className="g-2 align-items-end">
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">From</label>
                <FormControl
                  className="bk-input"
                  type="date"
                  value={dateFrom}
                  max={dateTo}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </Col>
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">To</label>
                <FormControl
                  className="bk-input"
                  type="date"
                  value={dateTo}
                  min={dateFrom}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </Col>
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">Status</label>
                <Dropdown>
                  <Dropdown.Toggle className="bk-filter-btn w-100">
                    <span>
                      <i className="bi bi-funnel me-2" />
                      {labelOf(STATUS_OPTIONS, statusFilter, "Status")}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {STATUS_OPTIONS.map((o) => (
                      <Dropdown.Item
                        key={o.value}
                        active={statusFilter === o.value}
                        onClick={() => setStatusFilter(o.value)}
                      >
                        {o.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">Method</label>
                <Dropdown>
                  <Dropdown.Toggle className="bk-filter-btn w-100">
                    <span>
                      <i className="bi bi-credit-card me-2" />
                      {labelOf(METHOD_OPTIONS, methodFilter, "Method")}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {METHOD_OPTIONS.map((o) => (
                      <Dropdown.Item
                        key={o.value}
                        active={methodFilter === o.value}
                        onClick={() => setMethodFilter(o.value)}
                      >
                        {o.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={6} md={3} lg={2}>
                <label className="bk-input-label">Type</label>
                <Dropdown>
                  <Dropdown.Toggle className="bk-filter-btn w-100">
                    <span>
                      <i className="bi bi-tag me-2" />
                      {labelOf(TYPE_OPTIONS, typeFilter, "Type")}
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    {TYPE_OPTIONS.map((o) => (
                      <Dropdown.Item
                        key={o.value}
                        active={typeFilter === o.value}
                        onClick={() => setTypeFilter(o.value)}
                      >
                        {o.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
              <Col xs={6} md={3} lg={2}>
                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    className="bk-btn-primary flex-grow-1"
                    disabled={loading}
                    style={{ height: 42 }}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Apply"
                    )}
                  </Button>
                  <Button
                    type="button"
                    className="bk-btn-ghost"
                    onClick={handleResetFilters}
                    disabled={loading}
                    title="Reset filters"
                    style={{ height: 42 }}
                  >
                    <i className="bi bi-arrow-counterclockwise" />
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>

        {/* ---------- Table ---------- */}
        <div className="bk-card">
          <div className="bk-card-header">
            <h5>
              <i
                className="bi bi-clock-history"
                style={{ color: Colors.PRIMARY }}
              />
              All Payments
            </h5>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <small>
                {pagination.total > 0
                  ? `Page ${pagination.current_page} of ${pagination.last_page} · ${pagination.total} total`
                  : "No records"}
              </small>
              <Button
                className="bk-btn-ghost"
                type="button"
                onClick={exportReport}
                disabled={loading || exporting}
              >
                {exporting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Exporting…
                  </>
                ) : (
                  <>
                    <i className="bi bi-download me-1" />
                    Export CSV
                  </>
                )}
              </Button>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 20 }}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bk-skel mb-3" style={{ height: 42 }} />
              ))}
            </div>
          ) : payments.length === 0 ? (
            <div className="bk-empty">
              <div className="bk-empty-icon">
                <i className="bi bi-clock-history" />
              </div>
              <h5 style={{ color: Colors.TEXT_DARK }}>No payment history</h5>
              <p className="text-muted mb-0">
                No payments found for the selected filters.
              </p>
            </div>
          ) : (
            <>
              <Table responsive hover className="bk-table">
                <thead>
                  <tr>
                    <th>Payment</th>
                    <th>Customer</th>
                    <th>Operator</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Type</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => {
                    const refundState = getRefundState(p);
                    const refunded = refundState === "refunded";
                    const customer = getPassengerName(p);

                    return (
                      <tr
                        key={getPaymentId(p)}
                        className={getRowClass(p)}
                        onClick={() => viewDetails(p)}
                      >
                        <td>
                          <span className="bk-id-chip">#{getPaymentId(p)}</span>
                          <div className="bk-sub">Rental #{p.rental_id}</div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Avatar name={customer} />
                            <div className="bk-name">{customer}</div>
                          </div>
                        </td>
                        <td>{getOperatorName(p)}</td>
                        <td style={{ textAlign: "right" }}>
                          <span
                            className={`bk-money ${refunded ? "muted" : ""}`}
                          >
                            {formatCurrency(p.amount)}
                          </span>
                        </td>
                        <td>
                          <TypeChip type={getPaymentType(p)} />
                        </td>
                        <td>
                          <MethodPill method={p.payment_method} />
                        </td>
                        <td>
                          <div className="d-flex flex-column align-items-start gap-1">
                            <StatusPill
                              status={refunded ? "refunded" : p.status}
                            />
                            <RefundBadge
                              state={refundState}
                              amount={getRefundInfo(p).refund_amount}
                            />
                          </div>
                        </td>
                        <td>
                          <span
                            style={{ color: Colors.TEXT_LIGHT, fontSize: 13 }}
                          >
                            {formatShortDate(getPaymentDate(p))}
                          </span>
                        </td>
                        <td
                          className="col-actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            className="bk-btn-ghost"
                            size="sm"
                            onClick={() => viewDetails(p)}
                          >
                            <i className="bi bi-eye me-1" />
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              <PaginationBar
                pagination={pagination}
                loading={loading}
                onPage={handlePageChange}
              />
            </>
          )}
        </div>

        <PaymentDetailsModal
          payment={selectedPayment}
          show={detailOpen}
          onHide={() => setDetailOpen(false)}
        />
      </Container>
    </div>
  );
};

export default PaymentsHistory;
